import React, { useEffect, useState } from "react";
import { GetRegisteredHGOs } from "../../../../Actions/AuthorizationActions";
import { CreateAccumulativeRequest } from "../../../../Actions/LedgerActions";
import { GetMonazamDetails } from "../../../../Actions/RequestActions";
import { getCurrentDate, SuccessToast, WarningToast } from "../../../../Util";
import { FaPlus, FaTimes } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import "./ReversalAccumulativeRequest.css";

const ReversalAccumulativeRequestContainer = () => {
  const [values, setValues] = useState({
    bankingFee: 0,
    totalAmount: null,
    narration: "",
  });

  const [type, setType] = useState("");
  const [currency, setCurrency] = useState("");
  const [monazamCurrency, setMonazamCurrency] = useState("");
  const [monazamBankAccountId, setMonazamBankAccountId] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [hgos, setHGOs] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const [rows, setRows] = useState([
    {
      id: "",
      num: 0,
      amount: "",
    },
  ]);

  const addNewRow = () => {
    setRows([
      ...rows,
      {
        id: "",
        num: rows.length,
        amount: "",
      },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length > 1) {
      const updatedRows = rows.filter((_, i) => i !== index);
      setRows(updatedRows);
      
      const total = updatedRows.reduce((sum, row) => {
        return sum + (parseFloat(row.amount) || 0);
      }, 0);
      setValues({ ...values, totalAmount: total });
    }
  };

  const handleCreate = () => {
    SuccessToast("Reversal accumulative request created successfully.");
    setValues({
      bankingFee: 0,
      totalAmount: 0,
      narration: "",
    });
    setRows([
      {
        id: "",
        num: 0,
        amount: "",
      },
    ]);
    setType("");
    setCurrency("");
    setMonazamCurrency("");
    setMonazamBankAccountId("");
    setValidationErrors({});
  };

  const handleSubmit = () => {
    const errors = {};

    if (!type) errors.type = true;
    if (!currency) errors.currency = true;
    if (!values.narration?.trim()) errors.narration = true;
    // Allow zero banking fees - only error if null, undefined, or negative
    if (values.bankingFee === null || values.bankingFee === undefined || (values.bankingFee !== 0 && values.bankingFee < 0)) {
      errors.bankingFee = true;
    }

    rows.forEach((row) => {
      if (!row.id || row.id === "") {
        errors[`hgo_${row.num}`] = true;
      }
      if (!row.amount || parseFloat(row.amount) <= 0) {
        errors[`amount_${row.num}`] = true;
      }
    });

    if (
      (type === "OpapToMonazamTransfer" || type === "EhajjToMonazamTransfer") &&
      !monazamCurrency
    ) {
      errors.monazamCurrency = true;
    }

    if (
      (type === "OpapToMonazamTransfer" || type === "EhajjToMonazamTransfer") &&
      monazamCurrency === "PKR" &&
      !monazamBankAccountId
    ) {
      errors.monazamBankAccountId = true;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      const totalAmount = values.totalAmount + values.bankingFee;
      const requestData = {
        ...values,
        totalAmount: totalAmount,
        bankingFee: values.bankingFee,
        narration: values.narration.trim(),
        categoryType: type,
        date: new Date().toISOString().split('T')[0],
        currency: currency,
        users: rows.map((row) => ({
          id: row.id,
          amount: parseFloat(row.amount)
        }))
      };

      if (type === "OpapToMonazamTransfer" || type === "EhajjToMonazamTransfer") {
        requestData.monazamCurrency = monazamCurrency;
      }

      if (
        (type === "OpapToMonazamTransfer" || type === "EhajjToMonazamTransfer") &&
        monazamCurrency === "PKR" &&
        monazamBankAccountId
      ) {
        requestData.monazamBankAccountId = monazamBankAccountId;
      }

      CreateAccumulativeRequest(
        requestData,
        setLoading,
        setError,
        handleCreate
      );
    } else {
      WarningToast("Please fill in all required fields.");
    }
  };

  const handleTypeChange = (e) => {
    const selectedType = e.target.value;
    setType(selectedType);
    setValidationErrors({ ...validationErrors, type: false });
    
    setCurrency("SAR");
    
    setMonazamCurrency("");
    setMonazamBankAccountId("");
  };

  const handleCurrencyChange = (e) => {
    setCurrency(e.target.value);
    setValidationErrors({ ...validationErrors, currency: false });
  };

  const handleMonazamCurrencyChange = (e) => {
    setMonazamCurrency(e.target.value);
    setValidationErrors({ ...validationErrors, monazamCurrency: false });
    setMonazamBankAccountId("");
  };

  const handleBankAccountChange = (e) => {
    setMonazamBankAccountId(e.target.value);
    setValidationErrors({ ...validationErrors, monazamBankAccountId: false });
  };

  const handleHGOChange = (index, value) => {
    const updatedRows = rows.map((r, i) => 
      i === index ? { ...r, id: value } : r
    );
    setRows(updatedRows);
    setValidationErrors({ ...validationErrors, [`hgo_${rows[index].num}`]: false });
  };

  const handleAmountChange = (index, value) => {
    const cleanValue = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1").replace(/(\.\d{2})\d+/, "$1");
    
    const updatedRows = rows.map((r, i) => 
      i === index ? { ...r, amount: cleanValue } : r
    );
    setRows(updatedRows);
    
    const total = updatedRows.reduce((sum, r) => {
      return sum + (parseFloat(r.amount) || 0);
    }, 0);
    setValues({ ...values, totalAmount: total });
    
    setValidationErrors({ ...validationErrors, [`amount_${rows[index].num}`]: false });
  };

  const handleBankingFeeChange = (value) => {
    const cleanValue = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1").replace(/(\.\d{2})\d+/, "$1");
    setValues({ ...values, bankingFee: parseFloat(cleanValue) || 0 });
    setValidationErrors({ ...validationErrors, bankingFee: false });
  };

  const handleNarrationChange = (value) => {
    setValues({ ...values, narration: value });
    setValidationErrors({ ...validationErrors, narration: false });
  };

  function loadHGOs() {
    GetRegisteredHGOs(setHGOs, setError);
  }

  // Fetch bank accounts on component mount
  useEffect(() => {
    GetMonazamDetails(
      (data) => {
        if (data?.bankAccounts && Array.isArray(data.bankAccounts)) {
          const pkrAccounts = data.bankAccounts.filter(acc => acc.accountType === "PKR");
          setBankAccounts(pkrAccounts);
        }
      },
      setBankLoading,
      setBankError
    );
  }, []);

  useEffect(() => {
    loadHGOs();
  }, []);

  return (
    <div className="container-fluid main-content-table">
      <div className="row">
        <div className="col-12">
          <div className="bg-light p-2 rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h4 className="mb-0">Reversal Accumulative Request</h4>
              </div>
              <div className="d-flex flex-column">
                <label className="form-label small">Date</label>
                <input 
                  type="date" 
                  className="form-control date-input" 
                  value={getCurrentDate()} 
                  readOnly
                  onClick={(e) => e.target.showPicker()}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-body p-2">
                <div className="row mb-2">
                  <div className="col-md-6">
                    <label className="form-label small">Type *</label>
                    <select
                      className={`form-select ${validationErrors.type ? 'is-invalid' : ''}`}
                      value={type}
                      onChange={handleTypeChange}
                    >
                      <option value="">Select</option>
                      <option value="OpapToMonazamTransfer">OPAP to Monazam Transfer</option>
                      <option value="EhajjToMonazamTransfer">E-Hajj to Monazam Transfer</option>
                      <option value="EhajjToOpapTransfer">E-Hajj to OPAP Transfer</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Currency *</label>
                    {(() => {
                      if (type) {
                        return (
                          <input
                            type="text"
                            className="form-control"
                            value="SAR"
                            readOnly
                          />
                        );
                      }
                      
                      return (
                        <>
                          <select
                            className={`form-select ${validationErrors.currency ? 'is-invalid' : ''}`}
                            value={currency}
                            onChange={handleCurrencyChange}
                            disabled
                          >
                            <option value="">Select</option>
                          </select>
                          <small className="text-muted">
                            * Select the Type First
                          </small>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {(type === "OpapToMonazamTransfer" || type === "EhajjToMonazamTransfer") && (
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label className="form-label small">Currency of Monazzam *</label>
                      <select
                        className={`form-select ${validationErrors.monazamCurrency ? 'is-invalid' : ''}`}
                        value={monazamCurrency}
                        onChange={handleMonazamCurrencyChange}
                      >
                        <option value="">Select</option>
                        <option value="PKR">PKR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    {/* Monazzam Account dropdown - only show for PKR in transfers to Monazam */}
                    {monazamCurrency === "PKR" && (
                      <div className="col-md-6">
                        <label className="form-label small">Monazzam Account *</label>
                        <select
                          className={`form-select ${validationErrors.monazamBankAccountId ? 'is-invalid' : ''}`}
                          value={monazamBankAccountId}
                          onChange={handleBankAccountChange}
                          disabled={bankLoading || !monazamCurrency}
                        >
                          <option value="" disabled hidden>
                            {bankLoading ? "Loading accounts..." : "Select"}
                          </option>
                          {bankAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.bankName || account.accountTitle || account.iban}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {rows.map((row, index) => (
                  <div key={index} className="row mb-2">
                    <div className="col-12">
                      <div className="p-2 rounded transfer-row">
                        <div className="row">
                          <div className="col-md-5">
                            <label className="form-label small">HGO Company *</label>
                            <select
                              className={`form-select ${validationErrors[`hgo_${row.num}`] ? 'is-invalid' : ''}`}
                              value={row.id}
                              onChange={(e) => handleHGOChange(index, e.target.value)}
                            >
                              <option value="">Search</option>
                              {hgos
                                .filter((hgo) => {
                                  return !rows.some((r, i) => i !== index && r.id === hgo.id);
                                })
                                .map((hgo) => (
                                  <option key={hgo.id} value={hgo.id}>
                                    {hgo.name}
                                  </option>
                                ))}
                            </select>
                          </div>
                          
                          <div className="col-md-5">
                            <label className="form-label small">Amount *</label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors[`amount_${row.num}`] ? 'is-invalid' : ''}`}
                              placeholder="20,555"
                              value={row.amount ? Number(row.amount).toLocaleString() : ""}
                              onChange={(e) => handleAmountChange(index, e.target.value)}
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            {rows.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm remove-btn"
                                onClick={() => removeRow(index)}
                              >
                                <FaTimes className="me-1" />
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="row mb-2">
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-link text-success p-0 add-button"
                      onClick={addNewRow}
                    >
                      <FaPlus className="me-1" />
                      Add
                    </button>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <label className="form-label small">Banking Fee *</label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.bankingFee ? 'is-invalid' : ''}`}
                      placeholder="2,000"
                      value={values.bankingFee !== null && values.bankingFee !== undefined ? Number(values.bankingFee).toLocaleString() : ""}
                      onChange={(e) => handleBankingFeeChange(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Total *</label>
                    <input
                      type="text"
                      className="form-control total-input"
                      value={(values.totalAmount !== null && values.totalAmount !== undefined) || (values.bankingFee !== null && values.bankingFee !== undefined) ? Number((values.totalAmount || 0) + (values.bankingFee || 0)).toLocaleString() : ""}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-12">
                    <label className="form-label small">Narration *</label>
                    <textarea
                      className={`form-control ${validationErrors.narration ? 'is-invalid' : ''}`}
                      rows="3"
                      placeholder="Comment Here"
                      value={values.narration}
                      onChange={(e) => handleNarrationChange(e.target.value)}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button
                    className="btn btn-success btn-lg px-5 submit-button"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Submit"
                      )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReversalAccumulativeRequestContainer;
